import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appService } from "../../App/app.service";

// Drilldown "locations" used by the existing RCC modals.
const LOCATIONS = {
  QUALIFIED_PIPELINE: "drilldownOnGraph",
  WON_OPPORTUNITIES: "drilldownOnGraphAttainment",
  OPP_DETAILS: "drilldownOppId",
};

// Action keys cards can call: onDrilldown(action, payload)
export const RCC_DRILLDOWN_ACTIONS = {
  DEFAULT: "default",
  WON_OPPORTUNITIES: "wonOpportunities",
  QUALIFIED_PIPELINE: "qualifiedPipeline",
};

/**
 * RCC drilldown controller.
 * Keeps drilldown modal state + async data fetching out of `Index.jsx`.
 *
 * Inputs: none (reads redux + uses appService)
 * Outputs: handlers + state needed to render drilldown modals
 */
export default function useRccDrilldowns() {
  const dispatch = useDispatch();

  // Drilldown state
  const [drilldownLocation, setDrilldownLocation] = useState("");
  const [secondLevelLocation, setSecondLevelLocation] = useState("");
  // Card-supplied payload for Qtr Projection drilldown (ensembleData,
  // pipelineCoverageCFQ). Cards own this data in local state now, so we
  // stash the latest values when the drilldown opens and forward them
  // to the modal.
  const [qtrProjectionPayload, setQtrProjectionPayload] = useState({});

  // Redux selectors for drilldown data
  const drilldownOnGraphData = useSelector(
    (state) => state.app.drilldownOnGraphData
  );
  const ExpectedAcvData = useSelector((state) => state.app.ExpectedAcvData);
  const Value_Label = useSelector((state) => state.app.user?.Value_Label);
  const company = useSelector((state) => state.app.user?.company);
  const Win_Rate_Prefix = useSelector(
    (state) => state.app.user?.Win_Rate_Prefix
  );
  const rccViewAsTeam = useSelector(
    (state) => state.app?.rccViewAsTeam ?? null
  );

  // Role + own-team populated by RccHeader's unfiltered homepageData call in Redux.
  // Drives default scoping: managers drill into their own team; admin/c-level get company-wide.
  const reduxHomepageData = useSelector((state) => state.app?.homepageData);

  // Build team filter array matching the pattern used by QtdBookingsCard / YtdBookingsCard
  const teamFilters = useMemo(() => {
    const rows = reduxHomepageData?.data || reduxHomepageData;
    const roleInfo = rows?.[4]?.[0];
    const roleType = roleInfo?.Role_Type;
    const teamName = roleInfo?.Team;

    if (rccViewAsTeam) {
      return [{ name: "Team", value: [rccViewAsTeam] }];
    }
    if (roleType === "manager" && teamName) {
      return [{ name: "Team", value: [teamName] }];
    }
    return [];
  }, [rccViewAsTeam, reduxHomepageData]);

  // Handler for QTD/YTD Bookings -> Won Opportunities drilldown
  const handleWonDrilldown = useCallback((timeFrame) => {
    setDrilldownLocation(LOCATIONS.WON_OPPORTUNITIES);
    dispatch({ type: "get_drilldown_on_graph_request" });

    appService.getAttainmentSectionDrilldown(teamFilters, timeFrame).then(
      (json) => {
        let tempOpportunities = json.message.opportunities[1]
          .sort((a, b) => (a.ACV > b.ACV ? -1 : 1))
          .map((item, index) => ({ ...item, OppNo: index + 1 }));

        json.message = {
          ...json,
          columns: json.message.columns,
          count_acv: json.message.opportunities[0],
          opportunities: tempOpportunities,
        };
        dispatch({ type: "get_drilldown_on_graph_success", json });
      },
      (error) => {
        dispatch({
          type: "get_drilldown_on_graph_failure",
          error: typeof error === "object" ? "Something went wrong" : error,
        });
      }
    );
  }, [dispatch, teamFilters]);

  // Handler for Qtr Projection -> Qualified Pipeline drilldown.
  // Payload shape: { ensembleData, pipelineCoverageCFQ }
  // The card passes its local state so the drilldown sees fresh,
  // team-scoped data (no stale Redux).
  const handleQualifiedPipelineDrilldown = useCallback((payload = {}) => {
    const ensembleData = payload.ensembleData;
    setQtrProjectionPayload({
      ensembleData,
      pipelineCoverageCFQ: payload.pipelineCoverageCFQ,
    });
    setDrilldownLocation(LOCATIONS.QUALIFIED_PIPELINE);
    dispatch({ type: "get_drilldown_on_graph_request" });

    if (ensembleData && ensembleData.expectedHighMidLow) {
      // Use time series drilldown for ensemble model
      appService.getTimeSeriesHomeDrilldown(teamFilters).then(
        (json) => {
          let tempOpportunities = json.message.opportunities[1]
            .map((item, index) => ({ ...item, OppNo: index + 1 }))
            .sort((a, b) =>
              a.Acv_At_Time_T > b.Acv_At_Time_T ? -1 : 1
            )
            .map((item, index) => ({ ...item, OppNo: index + 1 }));

          json.message = {
            ...json.message,
            count_acv: json.message.opportunities[0],
            opportunities: tempOpportunities,
            columns: json.message.columns,
          };
          dispatch({ type: "get_drilldown_on_graph_success", json });
        },
        (error) => {
          dispatch({
            type: "get_drilldown_on_graph_failure",
            error: typeof error === "object" ? "Something went wrong" : error,
          });
        }
      );
    } else {
      // Fallback to pipeline coverage drilldown
      appService
        .getPipelineCoverageGenericDrilldown(
          teamFilters,
          "open",
          "CFQ",
          undefined,
          undefined
        )
        .then(
          (json) => {
            let tempOpportunities = json.message.opportunities[1]
              .sort((a, b) => (a.ACV > b.ACV ? -1 : 1))
              .map((item, index) => ({ ...item, OppNo: index + 1 }));

            json.message = {
              ...json,
              columns: json.message.columns,
              count_acv: json.message.opportunities[0],
              opportunities: tempOpportunities,
            };
            dispatch({ type: "get_drilldown_on_graph_success", json });
          },
          (error) => {
            dispatch({
              type: "get_drilldown_on_graph_failure",
              error: typeof error === "object" ? "Something went wrong" : error,
            });
          }
        );
    }
  }, [dispatch, teamFilters]);

  // Back handler to close drilldowns
  const handleChangeLocationBack = useCallback(() => {
    setDrilldownLocation("");
    setSecondLevelLocation("");
    setQtrProjectionPayload({});
    dispatch({ type: "clear_expected_acv_data" });
  }, [dispatch]);

  // Second level drilldown handler (for clicking on individual opportunity).
  // Qualified pipeline uses legacy oppDetails (Expected Yield + Change Events);
  // Won opportunities uses waterfall/oppDetails.
  const handleSecondLevelDrilldown = useCallback(
    (location, oppId) => {
      setSecondLevelLocation(location);
      dispatch({ type: "get_expectedAcv_drilldown_data_request" });

      const useLegacyApi =
        drilldownLocation === LOCATIONS.QUALIFIED_PIPELINE;
      const fetchFn = useLegacyApi
        ? appService.getExpectedAcvData
        : appService.getWaterfallOppData;

      fetchFn(oppId).then(
        (json) => {
          dispatch({ type: "get_expectedAcv_drilldown_data_success", json });
        },
        (error) => {
          dispatch({
            type: "get_expectedAcv_drilldown_data_failure",
            error: typeof error === "object" ? "Something went wrong" : error,
          });
        }
      );
    },
    [dispatch, drilldownLocation]
  );

  // Step back from second level drilldown
  const stepBack = useCallback(() => {
    setSecondLevelLocation("");
    dispatch({ type: "clear_expected_acv_data" });
  }, [dispatch]);

  // CardId -> action -> handler mapping (extend this for new drilldowns).
  // Each card can have multiple drilldowns by calling onDrilldown(action, payload).
  // Memoized so handler lookup stays stable for parent memoized props.
  const cardDrilldownHandlers = useMemo(
    () => ({
      RCC_QTD_Bookings: {
        [RCC_DRILLDOWN_ACTIONS.DEFAULT]: () => handleWonDrilldown("CFQ"),
        [RCC_DRILLDOWN_ACTIONS.WON_OPPORTUNITIES]: (payload = {}) =>
          handleWonDrilldown(payload.timeFrame || "CFQ"),
      },
      RCC_YTD_Bookings: {
        [RCC_DRILLDOWN_ACTIONS.DEFAULT]: () => handleWonDrilldown("YTD"),
        [RCC_DRILLDOWN_ACTIONS.WON_OPPORTUNITIES]: (payload = {}) =>
          handleWonDrilldown(payload.timeFrame || "YTD"),
      },
      RCC_Qtr_Projection: {
        [RCC_DRILLDOWN_ACTIONS.DEFAULT]: handleQualifiedPipelineDrilldown,
        [RCC_DRILLDOWN_ACTIONS.QUALIFIED_PIPELINE]:
          handleQualifiedPipelineDrilldown,
      },
    }),
    [handleQualifiedPipelineDrilldown, handleWonDrilldown],
  );

  const getDrilldownHandler = useCallback((cardId) => {
    const actionMap = cardDrilldownHandlers[cardId];
    if (!actionMap) return undefined;

    // Compatible with:
    // - onClick={onDrilldown} (first arg is a synthetic event)
    // - onDrilldown() (no args)
    // - onDrilldown(action, payload)
    return (action, payload) => {
      const isEventLike =
        action &&
        typeof action === "object" &&
        typeof action.preventDefault === "function";

      const actionKey =
        isEventLike || !action ? RCC_DRILLDOWN_ACTIONS.DEFAULT : action;
      const handler =
        actionMap[actionKey] || actionMap[RCC_DRILLDOWN_ACTIONS.DEFAULT];
      if (typeof handler !== "function") return;

      if (isEventLike) return handler();
      return handler(payload);
    };
  }, [cardDrilldownHandlers]);

  // Return object is memoized to reduce churn for consumers that spread this hook result.
  return useMemo(
    () => ({
      // state
      drilldownLocation,
      secondLevelLocation,

      // handlers
      getDrilldownHandler,
      handleChangeLocationBack,
      handleSecondLevelDrilldown,
      stepBack,

      // data needed by modals
      drilldownOnGraphData,
      ExpectedAcvData,
      // Qtr Projection modal reads these from the click payload captured
      // when the user opened the drilldown — not from Redux.
      EnsembleData: qtrProjectionPayload.ensembleData,
      PipelineCoverageCFQ: qtrProjectionPayload.pipelineCoverageCFQ,
      rccViewAsTeam,
      Value_Label,
      company,
      Win_Rate_Prefix,

      // constants (optional but useful for consumers)
      LOCATIONS,
    }),
    [
      drilldownLocation,
      secondLevelLocation,
      getDrilldownHandler,
      drilldownOnGraphData,
      ExpectedAcvData,
      qtrProjectionPayload,
      rccViewAsTeam,
      Value_Label,
      company,
      Win_Rate_Prefix,
    ],
  );
}
